import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PlanComptableRow } from '@/lib/database.types';

export function usePlanComptable() {
  return useQuery({
    queryKey: ['plan_comptable'],
    queryFn: async (): Promise<PlanComptableRow[]> => {
      const { data, error } = await supabase.from('plan_comptable').select('*').order('numero');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertSubAccountInput {
  id?: string;
  parent_id: string;
  entreprise_id: string;
  numero: string;
  intitule: string;
  classe: number;
  description?: string | null;
}

export function useUpsertSubAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertSubAccountInput): Promise<PlanComptableRow> => {
      const { id, ...rest } = input;
      const payload = { ...rest, is_system: false };
      if (id) {
        const { data, error } = await supabase
          .from('plan_comptable')
          .update(payload)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase
        .from('plan_comptable')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['plan_comptable'] }),
  });
}

export function useDeleteSubAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('plan_comptable').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['plan_comptable'] }),
  });
}
